import { auth } from '../firebaseConfig';

/**
 * Authentication utility functions for API calls
 * 
 * This module provides helper functions to:
 * - Get authentication headers for API requests
 * - Handle authentication errors gracefully
 * - Manage token refresh automatically
 * - Validate subscription status before API calls
 * 
 * USAGE:
 * const response = await authenticatedFetch('/api/upload-pdf', { method: 'POST' });
 */

//** getAuthHeaders **//
/**
 * Get authentication headers for API requests
 * 
 * @returns {Promise<Object>} Headers object with Authorization Bearer token
 * @throws {Error} If user is not authenticated
 */
export const getAuthHeaders = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  try {
    const token = await user.getIdToken();
    return {
      'Authorization': `Bearer ${token}`
    };
  } catch (error) {
    console.error('Failed to get auth token:', error);
    throw new Error('Authentication failed');
  }
};

//** isAuthenticated **//
/**
 * Check if user is authenticated
 * 
 * @returns {boolean} True if user is logged in
 */
export const isAuthenticated = () => {
  return auth.currentUser !== null;
};

//** getCurrentUserId **//
/**
 * Get current user ID
 * 
 * @returns {string|null} User ID or null if not authenticated
 */
export const getCurrentUserId = () => {
  return auth.currentUser?.uid || null;
};

//** getCurrentUserEmail **//
/**
 * Get current user email
 * 
 * @returns {string|null} User email or null if not authenticated
 */
export const getCurrentUserEmail = () => {
  return auth.currentUser?.email || null;
};

//** handleAuthError **//
/**
 * Handle authentication errors with user-friendly messages
 * 
 * @param {Error} error - The error to handle
 * @param {string} operation - Description of the operation that failed
 * @returns {string} User-friendly error message
 */
export const handleAuthError = (error, operation = 'operation') => {
  console.error(`Authentication error during ${operation}:`, error);
  
  if (error.message.includes('not authenticated')) {
    return 'Please log in to continue.';
  } else if (error.message.includes('Authentication expired')) {
    return 'Your session has expired. Please log in again.';
  } else if (error.message.includes('Session expired')) {
    return 'Your session has expired. Please log in again.';
  } else if (error.message.includes('Authentication failed')) {
    return 'Authentication failed. Please try logging in again.';
  } else if (error.message.includes('Active subscription required')) {
    return 'You need an active subscription to access this feature.';
  } else if (error.message.includes('Subscription validation failed')) {
    return 'Unable to verify your subscription. Please try again or contact support.';
  } else {
    return `Failed to ${operation}. Please try again.`;
  }
};

//** authenticatedFetch **//
/**
 * Make authenticated API request with automatic token handling and subscription validation
 * 
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Response>} Fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
  try {
    const authHeaders = await getAuthHeaders();
    
    // Check subscription status before proceeding with API call
    // Skip subscription check for the subscription status endpoint itself to avoid infinite loops
    // Also skip while on the subscribe page to avoid redirect loops during onboarding
    if (!url.includes('/api/user/subscription-status') && !window.location.pathname.includes('/subscribe')) {
      // Skip subscription check for @gigemai.com users (admin users)
      const userEmail = getCurrentUserEmail();
      const isGigemaiUser = userEmail && userEmail.endsWith('@gigemai.com') && userEmail !== 'test_user@gigemai.com';
      
      if (!isGigemaiUser) {
        try {
          const subscriptionResponse = await fetch('/api/user/subscription-status', {
            headers: authHeaders
          });
          
          if (subscriptionResponse.ok) {
            const { subscription } = await subscriptionResponse.json();
            
            // Block API calls if user doesn't have active subscription
            if (subscription.status !== 'active') {
              console.warn('Subscription check failed: User does not have active subscription');
              window.location.href = '/subscribe';
              throw new Error('Active subscription required to access this feature');
            }
          } else if (subscriptionResponse.status === 500) {
            // 500 error = user document doesn't exist = new user
            console.log('New user detected - no subscription document found');
            // Don't redirect - let them access the page
            throw new Error('New user - no subscription document');
          } else if (subscriptionResponse.status === 401) {
            // 401 error = authentication failed = potential attack
            console.warn('Authentication failed during subscription check');
            window.location.href = '/login';
            throw new Error('Authentication failed');
          } else {
            // Other errors (network, etc.)
            console.warn('Subscription check failed: Could not verify subscription status');
            window.location.href = '/subscribe';
            throw new Error('Unable to verify subscription status');
          }
        } catch (subscriptionError) {
          // Only redirect if it's not a new user error
          if (!subscriptionError.message.includes('New user - no subscription document')) {
            console.error('Subscription validation failed:', subscriptionError);
            window.location.href = '/subscribe';
          }
          throw subscriptionError;
        }
      } else {
        console.log('Skipping subscription check for Gigemai user:', userEmail);
      }
    }
    
    // Proceed with original API call only if subscription is valid or is an api call for subscription creation.
    const response = await fetch(url, {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers
      }
    });

    // Handle authentication errors
    if (response.status === 401) {
      const errorData = await response.json().catch(() => ({}));
      
      if (errorData.code === 'TOKEN_EXPIRED') {
        // Token expired, try to refresh and retry once
        try {
          await auth.currentUser.getIdToken(true); // Force refresh
          const newAuthHeaders = await getAuthHeaders();
          
          const retryResponse = await fetch(url, {
            ...options,
            headers: {
              ...newAuthHeaders,
              ...options.headers
            }
          });
          
          return retryResponse;
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // Redirect to login if refresh fails
          window.location.href = '/login';
          throw new Error('Authentication expired. Please log in again.');
        }
      } else if (errorData.code === 'TOKEN_REVOKED') {
        // Token revoked, redirect to login
        window.location.href = '/login';
        throw new Error('Session expired. Please log in again.');
      } else {
        // Other auth errors
        throw new Error(errorData.error || 'Authentication failed');
      }
    }

    return response;
  } catch (error) {
    console.error('Authenticated fetch failed:', error);
    throw error;
  }
};

 